// ========================================
// AUDIT LOG COMPONENT - TRACK CHANGES
// ========================================

import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { getAuditLogs } from '../services/api';
import { getFeedbackLogs } from '../services/chatService';
import { onSocketEvent, SOCKET_EVENTS } from '../services/socketService';
import CustomDropdown from './shared/CustomDropdown';
import Button from './shared/Button';

// Admin users who can view feedback tab
const FEEDBACK_ADMINS = ['ian.w@aware.co.th', 'john.c@aware.co.th'];

/**
 * AuditLog Component
 *
 * Displays audit log entries showing who made changes, what was changed, and when.
 *
 * @param {Object} props - Component props
 * @param {Function} props.onClose - Function to close the modal
 * @param {string} props.currentUserEmail - Logged-in user's email from MS365
 * @param {Array} props.teams - List of teams for mapping IDs to names
 */
const AuditLog = ({ onClose, currentUserEmail, teams = [] }) => {
    const { t } = useTranslation();

    // Tab state - 'audit' or 'feedback'
    const [activeTab, setActiveTab] = useState('audit');

    // Check if current user is admin (can see feedback tab)
    const isAdmin = currentUserEmail && FEEDBACK_ADMINS.includes(currentUserEmail.toLowerCase());

    // Audit log state
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [totalLogs, setTotalLogs] = useState(0);
    const [dateRange, setDateRange] = useState('7days');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [actionFilter, setActionFilter] = useState('all');

    // Feedback state (admin only)
    const [feedbackLogs, setFeedbackLogs] = useState([]);
    const [feedbackLoading, setFeedbackLoading] = useState(false);
    const [feedbackError, setFeedbackError] = useState(null);
    const [feedbackRatingFilter, setFeedbackRatingFilter] = useState('all');
    const [feedbackDaysFilter, setFeedbackDaysFilter] = useState('30');

    // Date range options for CustomDropdown
    const dateRangeOptions = [
        { value: '1hour',  label: t('auditLog.last1Hour') },
        { value: '4hours', label: t('auditLog.last4Hours') },
        { value: '24hours',label: t('auditLog.last24Hours') },
        { value: 'today',  label: t('auditLog.today') },
        { value: '7days',  label: t('auditLog.last7Days') },
        { value: '30days', label: t('auditLog.last30Days') },
        { value: 'all',    label: t('auditLog.allTime') },
        { value: 'custom', label: t('auditLog.customRange') }
    ];

    // Action type options for CustomDropdown
    // Note: 'all' fetches without filter, categories filter client-side
    const actionFilterOptions = [
        { value: 'all', label: t('auditLog.allActions') },
        { value: 'budget', label: t('auditLog.budgetChanges') },
        { value: 'user', label: t('auditLog.userChanges') },
        { value: 'team', label: t('auditLog.teamChanges') }
    ];

    // Map filter categories to action types
    const ACTION_CATEGORIES = {
        budget: ['update_team_budget', 'create_team_budget', 'delete_team_budget', 'update_user_budget'],
        user: ['add_user_to_team', 'remove_user_from_team', 'disable_user_account', 'enable_user_account', 'update_user_permissions', 'create_user', 'delete_user', 'update_user'],
        team: ['block_team', 'unblock_team', 'create_team', 'delete_team', 'update_team', 'UPDATE_TEAM_THRESHOLDS', 'CLEAR_TEAM_THRESHOLDS']
    };

    /**
     * Action name mapping for user-friendly labels
     */
    const ACTION_MAP = {
        'update_team_budget': t('auditLog.actions.updateTeamBudget'),
        'create_team_budget': t('auditLog.actions.createTeamBudget'),
        'delete_team_budget': t('auditLog.actions.deleteTeamBudget'),
        'update_user_budget': t('auditLog.actions.updateUserBudget'),
        'create_user': t('auditLog.actions.createUser'),
        'delete_user': t('auditLog.actions.deleteUser'),
        'update_user': t('auditLog.actions.updateUser'),
        'create_team': t('auditLog.actions.createTeam'),
        'delete_team': t('auditLog.actions.deleteTeam'),
        'update_team': t('auditLog.actions.updateTeam'),
        'add_user_to_team': t('auditLog.actions.addUserToTeam'),
        'remove_user_from_team': t('auditLog.actions.removeUserFromTeam'),
        'block_team': t('auditLog.actions.blockTeam'),
        'unblock_team': t('auditLog.actions.unblockTeam'),
        'disable_user_account': t('auditLog.actions.disableUserAccount'),
        'enable_user_account': t('auditLog.actions.enableUserAccount'),
        'update_user_permissions': t('auditLog.actions.updateUserPermissions'),
        'login': t('auditLog.actions.login'),
        'logout': t('auditLog.actions.logout'),
        'UPDATE_TEAM_THRESHOLDS': t('auditLog.actions.updateTeamThresholds'),
        'CLEAR_TEAM_THRESHOLDS': t('auditLog.actions.clearTeamThresholds')
    };

    /**
     * Get team name from team ID
     */
    const getTeamName = useCallback((teamId) => {
        if (!teamId) return t('auditLog.unknownTeam');
        const team = teams.find(t => t.team_id === teamId);
        if (team) {
            return team.team_alias || team.team_name || teamId;
        }
        return teamId;
    }, [teams, t]);

    /**
     * Get friendly action name
     */
    const getFriendlyActionName = (action) => {
        if (!action) return t('auditLog.unknownAction');
        if (ACTION_MAP[action.toLowerCase()]) {
            return ACTION_MAP[action.toLowerCase()];
        }
        // Fallback: capitalize each word
        return action.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
    };

    /**
     * Calculate date filters based on date range selection
     */
    const getDateFilters = useCallback(() => {
        const filters = {};

        if (dateRange === '1hour') {
            const t1 = new Date(); t1.setHours(t1.getHours() - 1);
            filters.start_date = t1.toISOString();
        } else if (dateRange === '4hours') {
            const t4 = new Date(); t4.setHours(t4.getHours() - 4);
            filters.start_date = t4.toISOString();
        } else if (dateRange === '24hours') {
            const t24 = new Date(); t24.setHours(t24.getHours() - 24);
            filters.start_date = t24.toISOString();
        } else if (dateRange === 'today') {
            const tod = new Date();
            tod.setHours(0, 0, 0, 0);
            filters.start_date = tod.toISOString();
        } else if (dateRange === '7days') {
            const sevenDaysAgo = new Date();
            sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
            filters.start_date = sevenDaysAgo.toISOString();
        } else if (dateRange === '30days') {
            const thirtyDaysAgo = new Date();
            thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
            filters.start_date = thirtyDaysAgo.toISOString();
        } else if (dateRange === 'custom' && startDate && endDate) {
            filters.start_date = new Date(startDate).toISOString();
            filters.end_date = new Date(endDate).toISOString();
        }

        return filters;
    }, [dateRange, startDate, endDate]);

    /**
     * Fetch audit logs from the backend
     */
    const fetchLogs = useCallback(async (page = 1) => {
        setLoading(true);
        setError(null);

        try {
            // Fetch more logs to allow for client-side category filtering
            const filters = {
                page: page,
                page_size: actionFilter !== 'all' ? 100 : 20, // Fetch more if filtering client-side
                ...getDateFilters()
            };

            // Don't pass category filter to backend - we filter client-side
            // Backend expects exact action names, not categories

            const result = await getAuditLogs(filters);

            if (result.success) {
                let fetchedLogs = result.data.audit_logs || [];

                // Apply category filter client-side
                if (actionFilter !== 'all' && ACTION_CATEGORIES[actionFilter]) {
                    const allowedActions = ACTION_CATEGORIES[actionFilter];
                    fetchedLogs = fetchedLogs.filter(log =>
                        allowedActions.includes(log.action) ||
                        allowedActions.includes(log.action?.toLowerCase())
                    );
                }

                // Limit to 20 after filtering
                const displayLogs = fetchedLogs.slice(0, 20);

                setLogs(displayLogs);
                setTotalPages(Math.ceil(fetchedLogs.length / 20) || 1);
                setTotalLogs(fetchedLogs.length);
                setCurrentPage(page);
            } else {
                const errorMessage = result.error || 'Failed to load audit logs';
                if (errorMessage.includes('<!doctype') || errorMessage.includes('<!DOCTYPE')) {
                    setError('Backend server not responding. Please ensure the backend is running on port 5000.');
                } else if (errorMessage.includes('404') || errorMessage.includes('Not Found')) {
                    setError('Audit log endpoint not found. Please verify backend routes are loaded correctly.');
                } else {
                    setError(errorMessage);
                }
                console.error('[AUDIT LOG] ✗ Error:', errorMessage);
            }
        } catch (err) {
            console.error('[AUDIT LOG] ✗ Exception:', err);
            if (err.message.includes('Failed to fetch') || err.message.includes('NetworkError')) {
                setError('Cannot connect to backend server. Please check if backend is running on port 5000.');
            } else if (err.message.includes('JSON')) {
                setError('Backend returned invalid response. Please restart the backend server.');
            } else {
                setError(err.message || 'An unexpected error occurred');
            }
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [actionFilter, getDateFilters]);

    /**
     * Fetch feedback logs (admin only)
     */
    const fetchFeedbackLogs = useCallback(async () => {
        if (!isAdmin) return;

        setFeedbackLoading(true);
        setFeedbackError(null);

        try {
            const options = {
                limit: 100
            };
            if (feedbackRatingFilter !== 'all') {
                options.rating = feedbackRatingFilter;
            }
            if (feedbackDaysFilter !== 'all') {
                options.days = parseInt(feedbackDaysFilter, 10);
            }

            const result = await getFeedbackLogs(options);

            if (result.success) {
                setFeedbackLogs(result.feedback || []);
            } else {
                setFeedbackError(result.error || 'Failed to load feedback');
            }
        } catch (err) {
            console.error('[FEEDBACK] Error:', err);
            setFeedbackError(err.message || 'Failed to load feedback');
        } finally {
            setFeedbackLoading(false);
        }
    }, [isAdmin, feedbackRatingFilter, feedbackDaysFilter]);

    // Fetch logs when filters change
    useEffect(() => {
        fetchLogs(1);
    }, [fetchLogs]);

    // Listen for real-time audit log updates
    useEffect(() => {
        const unsubAudit = onSocketEvent(SOCKET_EVENTS.AUDIT_NEW, () => {
            // Refresh audit logs when a new entry is added
            fetchLogs(currentPage);
        });

        const unsubFeedback = onSocketEvent(SOCKET_EVENTS.FEEDBACK_NEW, () => {
            // Refresh feedback logs when new feedback is submitted
            if (activeTab === 'feedback' && isAdmin) {
                fetchFeedbackLogs();
            }
        });

        return () => {
            unsubAudit();
            unsubFeedback();
        };
    }, [currentPage, activeTab, isAdmin, fetchLogs, fetchFeedbackLogs]);

    // Fetch feedback when tab is active and filters change
    useEffect(() => {
        if (activeTab === 'feedback' && isAdmin) {
            fetchFeedbackLogs();
        }
    }, [activeTab, isAdmin, fetchFeedbackLogs]);

    /**
     * Format timestamp into human-readable relative time
     */
    const formatTimestamp = (timestamp) => {
        if (!timestamp) return 'Unknown';

        const date = new Date(timestamp);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMins / 60);
        const diffDays = Math.floor(diffHours / 24);

        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
        if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
        if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;

        return date.toLocaleString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    /**
     * Generate a detailed description of the action from the log entry
     */
    const getActionDescription = (log) => {
        const action = log.action || log.action_type || 'Unknown Action';
        const details = log.details || {};

        // Helper to get team/group name from various possible fields
        const getTeamOrGroupName = () => {
            // Check group_name first (used by add/remove user actions)
            if (details.group_name) return details.group_name;
            // Then check team fields
            if (details.team_name) return details.team_name;
            if (details.team_alias) return details.team_alias;
            if (details.teamName) return details.teamName;
            // Try to look up by ID
            if (details.team_id) return getTeamName(details.team_id);
            return null;
        };

        // Handle add/remove user to/from team actions specifically
        if (action === 'add_user_to_team' || action === 'remove_user_from_team') {
            const teamName = getTeamOrGroupName() || 'Unknown Team';
            const userName = details.user_email || details.user_name || 'Unknown User';
            if (action === 'add_user_to_team') {
                return `Added ${userName} to ${teamName}`;
            } else {
                return `Removed ${userName} from ${teamName}`;
            }
        }

        // Handle budget-related actions
        if (action.toLowerCase().includes('budget')) {
            const teamName = getTeamOrGroupName() || 'Unknown Team';
            const newBudget = details.new_budget || details.max_budget;
            const oldBudget = details.old_budget;

            if (newBudget !== undefined && oldBudget !== undefined) {
                return `Updated ${teamName} budget: $${oldBudget} → $${newBudget}`;
            } else if (newBudget !== undefined) {
                return `Set ${teamName} budget to $${newBudget}`;
            }
            return `Modified ${teamName} budget`;
        }

        // Handle team-related actions (block, unblock, thresholds, etc.)
        if (action.toLowerCase().includes('team') || action.includes('TEAM')) {
            const teamName = getTeamOrGroupName() || 'Unknown Team';
            if (action.toLowerCase().includes('block') && !action.toLowerCase().includes('unblock')) {
                return `Blocked team: ${teamName}`;
            } else if (action.toLowerCase().includes('unblock')) {
                return `Unblocked team: ${teamName}`;
            } else if (action.includes('THRESHOLD')) {
                return `Updated thresholds for: ${teamName}`;
            } else if (action.toLowerCase().includes('create')) {
                return `Created team: ${teamName}`;
            } else if (action.toLowerCase().includes('delete')) {
                return `Deleted team: ${teamName}`;
            } else {
                return `Modified team: ${teamName}`;
            }
        }

        // Handle user-related actions (disable/enable account)
        if (action.toLowerCase().includes('user') || action.toLowerCase().includes('account')) {
            const userName = details.user_email || details.user_name || details.user_id || 'Unknown User';
            if (action.toLowerCase().includes('disable')) {
                return `Disabled account: ${userName}`;
            } else if (action.toLowerCase().includes('enable')) {
                return `Enabled account: ${userName}`;
            } else if (action.toLowerCase().includes('create') || action.toLowerCase().includes('add')) {
                return `Added user: ${userName}`;
            } else if (action.toLowerCase().includes('delete') || action.toLowerCase().includes('remove')) {
                return `Removed user: ${userName}`;
            } else {
                return `Modified user: ${userName}`;
            }
        }

        return getFriendlyActionName(action);
    };

    /**
     * Get color for action badge based on action type
     */
    const getActionBadgeColor = (log) => {
        const action = (log.action || log.action_type || '').toLowerCase();
        if (action.includes('budget')) return '#3498db';
        if (action.includes('create') || action.includes('add')) return '#27ae60';
        if (action.includes('delete') || action.includes('remove')) return '#e74c3c';
        if (action.includes('user')) return '#9b59b6';
        if (action.includes('team')) return '#e67e22';
        return 'var(--muted)';
    };

    /**
     * Format date to UK format (DD/MM/YYYY HH:mm)
     */
    const formatDateUK = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '';

        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');

        return `${day}/${month}/${year} ${hours}:${minutes}`;
    };

    /**
     * Export audit logs to CSV file
     */
    const handleExport = () => {
        if (logs.length === 0) {
            alert('No logs to export');
            return;
        }

        const headers = ['Timestamp', 'User', 'Action', 'Details'];
        const rows = logs.map(log => [
            formatDateUK(log.timestamp || log.created_at),
            log.user_email || log.user || 'System',
            getFriendlyActionName(log.action || log.action_type || ''),
            getActionDescription(log)
        ]);

        const csvContent = [
            headers.join(','),
            ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
        ].join('\n');

        // Add UTF-8 BOM to ensure Excel renders special characters correctly
        const bomPrefix = '\uFEFF';
        const blob = new Blob([bomPrefix + csvContent], { type: 'text/csv;charset=utf-8;' });

        // Generate filename with UK date format (DD-MM-YYYY)
        const now = new Date();
        const day = now.getDate().toString().padStart(2, '0');
        const month = (now.getMonth() + 1).toString().padStart(2, '0');
        const year = now.getFullYear();
        const filename = `audit-logs-${day}-${month}-${year}.csv`;

        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
    };

    /**
     * Export feedback logs to CSV file
     */
    const handleFeedbackExport = () => {
        if (feedbackLogs.length === 0) {
            alert('No feedback to export');
            return;
        }

        // Truncate text helper
        const truncate = (text, maxLen = 200) => {
            if (!text) return '';
            const cleaned = text.replace(/"/g, '""').replace(/\n/g, ' ');
            return cleaned.length > maxLen ? cleaned.substring(0, maxLen) + '...' : cleaned;
        };

        const headers = ['Timestamp', 'User', 'Rating', 'Question', 'AI Response', 'Comment'];
        const rows = feedbackLogs.map(fb => [
            formatDateUK(fb.timestamp),
            fb.userEmail || 'Unknown',
            fb.rating === 'positive' ? 'Positive' : 'Negative',
            truncate(fb.question),
            truncate(fb.response),
            truncate(fb.comment || '', 500)
        ]);

        const csvContent = [
            headers.join(','),
            ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
        ].join('\n');

        const bomPrefix = '\uFEFF';
        const blob = new Blob([bomPrefix + csvContent], { type: 'text/csv;charset=utf-8;' });

        const now = new Date();
        const day = now.getDate().toString().padStart(2, '0');
        const month = (now.getMonth() + 1).toString().padStart(2, '0');
        const year = now.getFullYear();
        const filename = `chat-feedback-${day}-${month}-${year}.csv`;

        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
    };

    return (
        <>
            {/* Modal overlay */}
            <div className="modal-overlay" onClick={onClose} />

            {/* Main modal container */}
            <div className="modal" style={{ maxWidth: '1000px', maxHeight: '90vh', overflow: 'auto' }}>

                {/* Modal header */}
                <div className="modal-header">
                    <h2 className="modal-title">
                        {activeTab === 'audit' ? t('auditLog.title') : t('auditLog.feedbackTitle', 'Chat Feedback')}
                    </h2>
                    <button onClick={onClose} className="modal-close">✕</button>
                </div>

                {/* Modal body */}
                <div className="modal-body">

                    {/* Tab switcher - only show if admin */}
                    {isAdmin && (
                        <div style={{
                            display: 'flex',
                            gap: '0',
                            marginBottom: '1.5rem',
                            borderBottom: '2px solid var(--border)'
                        }}>
                            <button
                                onClick={() => setActiveTab('audit')}
                                style={{
                                    padding: '12px 24px',
                                    backgroundColor: 'transparent',
                                    border: 'none',
                                    borderBottom: activeTab === 'audit' ? '2px solid var(--surface-alt)' : '2px solid transparent',
                                    marginBottom: '-2px',
                                    color: activeTab === 'audit' ? 'var(--fg-primary)' : 'var(--fg-secondary)',
                                    fontWeight: activeTab === 'audit' ? '600' : '400',
                                    fontSize: '0.9375rem',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease'
                                }}
                            >
                                {t('auditLog.tabAudit', 'Audit Log')}
                            </button>
                            <button
                                onClick={() => setActiveTab('feedback')}
                                style={{
                                    padding: '12px 24px',
                                    backgroundColor: 'transparent',
                                    border: 'none',
                                    borderBottom: activeTab === 'feedback' ? '2px solid var(--surface-alt)' : '2px solid transparent',
                                    marginBottom: '-2px',
                                    color: activeTab === 'feedback' ? 'var(--fg-primary)' : 'var(--fg-secondary)',
                                    fontWeight: activeTab === 'feedback' ? '600' : '400',
                                    fontSize: '0.9375rem',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease'
                                }}
                            >
                                {t('auditLog.tabFeedback', 'Chat Feedback')}
                            </button>
                        </div>
                    )}

                    {/* Display logged-in user */}
                    <div className="preview-box" style={{ marginBottom: '1.5rem' }}>
                        <strong>{t('auditLog.loggedInAs')}:</strong> {currentUserEmail || t('auditLog.unknownUser')}
                    </div>

                    {/* AUDIT LOG TAB CONTENT */}
                    {activeTab === 'audit' && (
                        <>
                            {/* Filter controls */}
                            <div className="form-section" style={{ marginBottom: '1.5rem' }}>
                                <h3>{t('auditLog.filters')}</h3>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>

                                    {/* Date range filter */}
                                    <div className="input-group">
                                        <label className="input-label">{t('auditLog.dateRange')}</label>
                                        <CustomDropdown
                                            value={dateRange}
                                            options={dateRangeOptions}
                                            onChange={setDateRange}
                                            placeholder={t('auditLog.selectRange')}
                                        />
                                    </div>

                                    {/* Action type filter */}
                                    <div className="input-group">
                                        <label className="input-label">{t('auditLog.actionFilter')}</label>
                                        <CustomDropdown
                                            value={actionFilter}
                                            options={actionFilterOptions}
                                            onChange={setActionFilter}
                                            placeholder={t('auditLog.selectAction')}
                                        />
                                    </div>
                                </div>

                                {/* Custom date range inputs */}
                                {dateRange === 'custom' && (
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginTop: '1rem' }}>
                                        <div className="input-group">
                                            <label className="input-label">{t('auditLog.startDate')}</label>
                                            <input
                                                type="date"
                                                value={startDate}
                                                onChange={(e) => setStartDate(e.target.value)}
                                                className="form-input"
                                            />
                                        </div>
                                        <div className="input-group">
                                            <label className="input-label">{t('auditLog.endDate')}</label>
                                            <input
                                                type="date"
                                                value={endDate}
                                                onChange={(e) => setEndDate(e.target.value)}
                                                className="form-input"
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Stats bar */}
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: '1rem',
                                padding: '0.75rem',
                                backgroundColor: 'var(--bg)',
                                borderRadius: '6px',
                                border: '1px solid var(--border)'
                            }}>
                                <div style={{ fontSize: '0.875rem', color: 'var(--fg-secondary)' }}>
                                    {loading ? t('auditLog.loading') : t('auditLog.logsFound', { count: totalLogs })}
                                </div>
                                {logs.length > 0 && (
                                    <Button
                                        onClick={handleExport}
                                        variant="secondary"
                                        size="small"
                                    >
                                        {t('auditLog.exportCsv')}
                                    </Button>
                                )}
                            </div>

                            {/* Error message */}
                            {error && (
                                <div className="message message-error" style={{ marginBottom: '1rem' }}>
                                    {error}
                                </div>
                            )}

                            {/* Loading state */}
                            {loading && (
                                <div style={{ textAlign: 'center', padding: '2rem' }}>
                                    <div className="loading">{t('auditLog.loading')}</div>
                                </div>
                            )}

                            {/* Empty state */}
                            {!loading && !error && logs.length === 0 && (
                                <div className="preview-box" style={{
                                    textAlign: 'center',
                                    padding: '2rem',
                                    backgroundColor: 'var(--bg)',
                                    borderColor: 'var(--border)'
                                }}>
                                    <p style={{ margin: 0, color: 'var(--fg-secondary)', fontSize: '1rem' }}>
                                        {t('auditLog.noActivity')}
                                    </p>
                                    <p style={{ fontSize: '0.875rem', marginTop: '0.75rem', color: 'var(--fg-secondary)' }}>
                                        {t('auditLog.tryAdjusting')}
                                    </p>
                                </div>
                            )}

                            {/* Audit log table */}
                            {!loading && !error && logs.length > 0 && (
                                <div style={{ overflowX: 'auto' }}>
                                    <table style={{
                                        width: '100%',
                                        borderCollapse: 'collapse',
                                        fontSize: '0.875rem',
                                        backgroundColor: 'var(--bg)'
                                    }}>
                                        <thead>
                                            <tr style={{
                                                backgroundColor: 'var(--border)',
                                                borderBottom: '2px solid var(--border)'
                                            }}>
                                                <th style={{
                                                    padding: '0.75rem',
                                                    textAlign: 'left',
                                                    color: 'var(--fg-primary)',
                                                    fontWeight: '600'
                                                }}>{t('auditLog.timestamp')}</th>
                                                <th style={{
                                                    padding: '0.75rem',
                                                    textAlign: 'left',
                                                    color: 'var(--fg-primary)',
                                                    fontWeight: '600'
                                                }}>{t('auditLog.user')}</th>
                                                <th style={{
                                                    padding: '0.75rem',
                                                    textAlign: 'left',
                                                    color: 'var(--fg-primary)',
                                                    fontWeight: '600'
                                                }}>{t('auditLog.action')}</th>
                                                <th style={{
                                                    padding: '0.75rem',
                                                    textAlign: 'left',
                                                    color: 'var(--fg-primary)',
                                                    fontWeight: '600'
                                                }}>{t('auditLog.details')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {logs.map((log, index) => (
                                                <tr
                                                    key={log.id || index}
                                                    style={{
                                                        borderBottom: '1px solid var(--border)',
                                                        transition: 'background-color 0.2s'
                                                    }}
                                                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--border)'}
                                                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                                >
                                                    <td style={{
                                                        padding: '0.75rem',
                                                        whiteSpace: 'nowrap',
                                                        color: 'var(--fg-primary)'
                                                    }}>
                                                        {formatTimestamp(log.timestamp || log.created_at)}
                                                    </td>
                                                    <td style={{
                                                        padding: '0.75rem',
                                                        color: 'var(--fg-primary)'
                                                    }}>
                                                        <strong>{log.user_email || log.user || 'System'}</strong>
                                                    </td>
                                                    <td style={{ padding: '0.75rem' }}>
                                                        <span style={{
                                                            display: 'inline-block',
                                                            padding: '0.25rem 0.75rem',
                                                            borderRadius: '12px',
                                                            backgroundColor: getActionBadgeColor(log),
                                                            color: 'white',
                                                            fontSize: '0.75rem',
                                                            fontWeight: '600'
                                                        }}>
                                                            {getFriendlyActionName(log.action || log.action_type || 'Unknown')}
                                                        </span>
                                                    </td>
                                                    <td style={{
                                                        padding: '0.75rem',
                                                        color: 'var(--fg-primary)'
                                                    }}>
                                                        {getActionDescription(log)}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {/* Pagination controls */}
                            {!loading && !error && totalPages > 1 && (
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    gap: '1rem',
                                    marginTop: '1.5rem',
                                    padding: '1rem',
                                    backgroundColor: 'var(--bg)',
                                    borderRadius: '6px',
                                    border: '1px solid var(--border)'
                                }}>
                                    <Button
                                        onClick={() => fetchLogs(currentPage - 1)}
                                        disabled={currentPage === 1}
                                        variant="secondary"
                                        size="small"
                                    >
                                        Previous
                                    </Button>
                                    <span style={{ fontSize: '0.875rem', color: 'var(--fg-secondary)' }}>
                                        Page {currentPage} of {totalPages}
                                    </span>
                                    <Button
                                        onClick={() => fetchLogs(currentPage + 1)}
                                        disabled={currentPage === totalPages}
                                        variant="secondary"
                                        size="small"
                                    >
                                        Next
                                    </Button>
                                </div>
                            )}
                        </>
                    )}

                    {/* FEEDBACK TAB CONTENT - Admin only */}
                    {activeTab === 'feedback' && isAdmin && (
                        <>
                            {/* Feedback filters */}
                            <div className="form-section" style={{ marginBottom: '1.5rem' }}>
                                <h3>{t('auditLog.feedbackFilters', 'Filters')}</h3>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                    <div className="input-group">
                                        <label className="input-label">{t('auditLog.feedbackRating', 'Rating')}</label>
                                        <CustomDropdown
                                            value={feedbackRatingFilter}
                                            options={[
                                                { value: 'all', label: t('auditLog.feedbackAllRatings', 'All Ratings') },
                                                { value: 'positive', label: t('auditLog.feedbackPositive', 'Positive') },
                                                { value: 'negative', label: t('auditLog.feedbackNegative', 'Negative') }
                                            ]}
                                            onChange={setFeedbackRatingFilter}
                                        />
                                    </div>
                                    <div className="input-group">
                                        <label className="input-label">{t('auditLog.feedbackPeriod', 'Period')}</label>
                                        <CustomDropdown
                                            value={feedbackDaysFilter}
                                            options={[
                                                { value: '7', label: t('auditLog.last7Days') },
                                                { value: '30', label: t('auditLog.last30Days') },
                                                { value: '90', label: t('auditLog.last90Days', 'Last 90 Days') },
                                                { value: 'all', label: t('auditLog.allTime') }
                                            ]}
                                            onChange={setFeedbackDaysFilter}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Stats bar */}
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: '1rem',
                                padding: '0.75rem',
                                backgroundColor: 'var(--bg)',
                                borderRadius: '6px',
                                border: '1px solid var(--border)'
                            }}>
                                <div style={{ fontSize: '0.875rem', color: 'var(--fg-secondary)' }}>
                                    {feedbackLoading
                                        ? t('auditLog.loading')
                                        : t('auditLog.feedbackFound', '{{count}} feedback entries', { count: feedbackLogs.length })
                                    }
                                </div>
                                {feedbackLogs.length > 0 && (
                                    <Button
                                        onClick={handleFeedbackExport}
                                        variant="secondary"
                                        size="small"
                                    >
                                        {t('auditLog.exportCsv')}
                                    </Button>
                                )}
                            </div>

                            {/* Error message */}
                            {feedbackError && (
                                <div className="message message-error" style={{ marginBottom: '1rem' }}>
                                    {feedbackError}
                                </div>
                            )}

                            {/* Loading state */}
                            {feedbackLoading && (
                                <div style={{ textAlign: 'center', padding: '2rem' }}>
                                    <div className="loading">{t('auditLog.loading')}</div>
                                </div>
                            )}

                            {/* Empty state */}
                            {!feedbackLoading && !feedbackError && feedbackLogs.length === 0 && (
                                <div className="preview-box" style={{
                                    textAlign: 'center',
                                    padding: '2rem',
                                    backgroundColor: 'var(--bg)',
                                    borderColor: 'var(--border)'
                                }}>
                                    <p style={{ margin: 0, color: 'var(--fg-secondary)', fontSize: '1rem' }}>
                                        {t('auditLog.noFeedback', 'No feedback collected yet')}
                                    </p>
                                </div>
                            )}

                            {/* Feedback table */}
                            {!feedbackLoading && !feedbackError && feedbackLogs.length > 0 && (
                                <div style={{ overflowX: 'auto' }}>
                                    <table style={{
                                        width: '100%',
                                        borderCollapse: 'collapse',
                                        fontSize: '0.875rem',
                                        backgroundColor: 'var(--bg)'
                                    }}>
                                        <thead>
                                            <tr style={{
                                                backgroundColor: 'var(--border)',
                                                borderBottom: '2px solid var(--border)'
                                            }}>
                                                <th style={{ padding: '0.75rem', textAlign: 'left', color: 'var(--fg-primary)', fontWeight: '600' }}>
                                                    {t('auditLog.timestamp')}
                                                </th>
                                                <th style={{ padding: '0.75rem', textAlign: 'left', color: 'var(--fg-primary)', fontWeight: '600' }}>
                                                    {t('auditLog.user')}
                                                </th>
                                                <th style={{ padding: '0.75rem', textAlign: 'left', color: 'var(--fg-primary)', fontWeight: '600' }}>
                                                    {t('auditLog.feedbackRating', 'Rating')}
                                                </th>
                                                <th style={{ padding: '0.75rem', textAlign: 'left', color: 'var(--fg-primary)', fontWeight: '600' }}>
                                                    {t('auditLog.feedbackQuestion', 'Question')}
                                                </th>
                                                <th style={{ padding: '0.75rem', textAlign: 'left', color: 'var(--fg-primary)', fontWeight: '600' }}>
                                                    {t('auditLog.feedbackComment', 'Comment')}
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {feedbackLogs.map((fb, index) => (
                                                <tr
                                                    key={fb.id || index}
                                                    style={{
                                                        borderBottom: '1px solid var(--border)',
                                                        transition: 'background-color 0.2s'
                                                    }}
                                                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--border)'}
                                                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                                >
                                                    <td style={{ padding: '0.75rem', whiteSpace: 'nowrap', color: 'var(--fg-primary)' }}>
                                                        {formatTimestamp(fb.timestamp)}
                                                    </td>
                                                    <td style={{ padding: '0.75rem', color: 'var(--fg-primary)' }}>
                                                        <strong>{fb.userEmail || 'Unknown'}</strong>
                                                    </td>
                                                    <td style={{ padding: '0.75rem' }}>
                                                        <span style={{
                                                            display: 'inline-block',
                                                            padding: '0.25rem 0.75rem',
                                                            borderRadius: '12px',
                                                            backgroundColor: fb.rating === 'positive' ? '#22c55e' : '#ef4444',
                                                            color: 'white',
                                                            fontSize: '0.75rem',
                                                            fontWeight: '600'
                                                        }}>
                                                            {fb.rating === 'positive'
                                                                ? t('auditLog.feedbackPositive', 'Positive')
                                                                : t('auditLog.feedbackNegative', 'Negative')
                                                            }
                                                        </span>
                                                    </td>
                                                    <td style={{
                                                        padding: '0.75rem',
                                                        color: 'var(--fg-primary)',
                                                        maxWidth: '250px',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap'
                                                    }} title={fb.question}>
                                                        {fb.question?.substring(0, 60)}{fb.question?.length > 60 ? '...' : ''}
                                                    </td>
                                                    <td style={{
                                                        padding: '0.75rem',
                                                        color: 'var(--fg-secondary)',
                                                        maxWidth: '200px',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                        fontStyle: fb.comment ? 'normal' : 'italic'
                                                    }} title={fb.comment}>
                                                        {fb.comment
                                                            ? (fb.comment.substring(0, 50) + (fb.comment.length > 50 ? '...' : ''))
                                                            : t('auditLog.noComment', 'No comment')
                                                        }
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* Modal footer */}
                <div className="modal-footer">
                    <Button onClick={onClose} variant="secondary">Close</Button>
                </div>
            </div>
        </>
    );
};

AuditLog.propTypes = {
    onClose: PropTypes.func.isRequired,
    currentUserEmail: PropTypes.string,
    teams: PropTypes.arrayOf(PropTypes.shape({
        team_id: PropTypes.string,
        team_alias: PropTypes.string
    }))
};

AuditLog.defaultProps = {
    currentUserEmail: '',
    teams: []
};

export default AuditLog;

